<template>
  <div>
    <!-- Hero Section -->
    <div class="w-full bg-primary text-white text-center tw-flex justify-center tw-flex flex-col px-4 py-8 md:py-12 h-[120px] md:h-[150px]">
      <h1 class="text-xl md:text-2xl lg:text-3xl font-bold mb-2">{{ onboardingTextConfig.personalHomeTitle }}</h1>
      <p class="text-sm md:text-base opacity-90">{{ onboardingTextConfig.personalHomeSubtitle }}</p>
    </div>

    <!-- Main Content -->
    <div class="tw-container px-4 md:px-6 lg:px-8">
      <div class="tw-flex flex-col lg:flex-row gap-4 lg:gap-6 xl:gap-8">
        <!-- Main Content Area -->
        <div class="w-full lg:w-3/4">
          <PasswordAndSecurity v-if="isPasswordAndSecurity"/>
          <PersonalHomeContent v-else/>
        </div>

        <!-- Sidebar -->
        <div class="w-full lg:w-1/4 mt-4 lg:mt-8">
          <ProfileCardsSection class="profile-cards-section"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import OnboardingMixin from '../../util/OnboardingMixin.js'
  import Tabs from '../Tabs/Tabs.vue'
  import { ACTION_TYPES as NOTIFICATION_ACTION_TYPES } from '../../store/modules/notifications.js'
  import Avatar from '../Avatar/Avatar.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import Dropdown from '../Dropdown/Dropdown.vue'
  import UiMixin from '../../util/UiMixin.js'
  import IntroJsMixin from '../../util/IntroJsMixin.js'

  import ToggleButton from '../Form/ToggleButton.vue'
  import { fetchLatestConversation } from '@/api/conversations'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { fetchLatestAnnouncements, fetchLatestChallenges, fetchLatestMembers } from '@/api/communities'
  import { fetchLatestActors } from '@/api/spottingareas'
  import { getUserPreferences } from '@/api/user'
  import CommunityMemberCard from '@/components/Communities/CommunityMemberCard.vue'
  import ActorMiniCard from '@/components/ActorMiniCard/ActorMiniCard.vue'
  import ChallengeCardNew from '@/components/Challenges/ChallengeCardNew.vue'
  import AnnouncementCard from '@/components/Simplified/AnnouncementCard.vue'
  import ActorInfoCard from '@/components/ActorInfo/ActorInfoCard.vue'
  import SimplifiedImageCard from '@/components/Connection/SimplifiedImageCard.vue'
  import ProfileCardsSection from '@/components/Profile/ProfileCardsSection.vue'
  import PersonalHomeContent from '@/components/Profile/PersonalHomeContent.vue'
  import PasswordAndSecurity from '@/components/Profile/PasswordAndSecurity.vue'

  export default {
    data () {
      return {
        componentKey: 1,
        portfolios: [],
        spottingAreas: [],
        finishedLoadingInitialData: false,
        conceptSearches: [],
        availableResources: [],
        relevantTags: [],
        acceptedTags: [],
        relevantTaxonomies: [],
        acceptedTaxonomies: [],
        updatingAllowPersonalisedContentOption: false,
        missedMessages: [],
        latestCommunityMembers: [],
        newSpottingAreaActors: [],
        challenges: [],
        announcements: [],
        userPreferences: {
          preferredSpottingAreaId: null,
          preferredCommunityId: null,
        },
      }
    },
    computed: {
      profile () {
        return this.$store.state.user.profile
      },
      hasAnyData () {
        return this.missedMessages.length > 0 ||
          this.latestCommunityMembers.length > 0 ||
          this.newSpottingAreaActors.length > 0 ||
          this.challenges.length > 0 ||
          this.announcements.length > 0
      },
      isPersonalHome () {
        console.log(this.$route.hash)
        return this.$route.hash === null
      },
      isPasswordAndSecurity () {
        return this.$route.hash === '#password-and-security'
      }
    },
    methods: {
      async loadUserPreferences () {
        try {
          const response = await getUserPreferences()
          this.userPreferences = response.preferences
        } catch (error) {
          console.error('Error loading user preferences:', error)
        }
      },
      getPreferredCommunityId () {
        // Use preferred community if set, otherwise fall back to first available
        if (this.userPreferences.preferredCommunityId) {
          return this.userPreferences.preferredCommunityId
        }
        if (this.profile && this.profile.joinedCommunityIds && this.profile.joinedCommunityIds.length > 0) {
          return this.profile.joinedCommunityIds[0]
        }
        return null
      },
      getPreferredSpottingAreaId () {
        // Use preferred spotting area if set, otherwise fall back to first available
        if (this.userPreferences.preferredSpottingAreaId) {
          return this.userPreferences.preferredSpottingAreaId
        }
        if (this.profile && this.profile.accessibleSpottingAreas && this.profile.accessibleSpottingAreas.length > 0) {
          return this.profile.accessibleSpottingAreas[0]
        }
        return null
      },
      handleClickActorCard (actor) {
        const spottingAreaId = this.getPreferredSpottingAreaId()
        this.$router.push(`/spotting-areas/${spottingAreaId}/actors/${actor.id}`)
      },
      openConversation (conversationId, userId) {
        this.$router.push('/profile/conversations/' + conversationId).then(() => {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
            component: 'user-information-side-panel',
            metaData: {
              userId: userId,
              allowDelete: false,
            },
          })
        })
      },
      fetchLatestConversationMessages () {
        fetchLatestConversation()
          .then((response) => {
            this.missedMessages = response
          })
      },
      fetchLatestCommunityMembers () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          fetchLatestMembers({ communityId })
            .then((response) => {
              this.latestCommunityMembers = response.filter((user) => {
                return user.id !== this.profile.id
              })
            })
        }
      },
      fetchNewSpottingAreaActors () {
        const spottingAreaId = this.getPreferredSpottingAreaId()
        if (spottingAreaId) {
          fetchLatestActors({ spottingAreaId })
            .then((response) => {
              this.newSpottingAreaActors = response.slice(0, 3) // Show latest 3 actors
            })
            .catch((error) => {
              console.error('Error fetching spotting area actors:', error)
            })
        }
      },
      fetchLatestChallenges () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          fetchLatestChallenges({ communityId, limit: 3 })
            .then((response) => {
              this.challenges = response
            })
            .catch((error) => {
              console.error('Error fetching challenges:', error)
            })
        }
      },
      fetchLatestAnnouncements () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          fetchLatestAnnouncements({ communityId, limit: 2 })
            .then((response) => {
              this.announcements = response
            })
            .catch((error) => {
              console.error('Error fetching announcements:', error)
            })
        }
      },
      formatMessageTime (timestamp) {
        return new Date(timestamp).toLocaleDateString()
      },
      async resetUserObjects () {
        // Refresh data when user profile is updated
        await this.loadUserPreferences()
      },
    },
    created () {
      this.$bus.on('updateUserProfile', this.resetUserObjects)
    },
    async mounted () {
      if (this.$route.hash === '#notification-settings') {
        this.$router.push('/notifications?o=true') // o = true will immediately open the sidepanel
        return
      }

      // Load user preferences first, then fetch data based on preferences
      await this.loadUserPreferences()
      this.$store.dispatch(NOTIFICATION_ACTION_TYPES.FETCH_NOTIFICATIONS_LIST, {
        limit: this.$store.state.notifications.listData.fetchLimit,
        offset: 0,
      })
    },
    beforeUnmount () {
      this.$bus.off('updateUserProfile', this.resetUserObjects)
    },
    mixins: [OnboardingMixin, TranslationsMixin, UiMixin, IntroJsMixin],
    components: {
      PasswordAndSecurity,
      PersonalHomeContent,
      ProfileCardsSection,
      SimplifiedImageCard,
      ActorInfoCard,
      AnnouncementCard,
      ChallengeCardNew,
      ActorMiniCard,
      CommunityMemberCard,
      AvatarNew,
      Tabs,
      Avatar,
      Dropdown,
      ToggleButton
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/_variables.scss";

  .ph__allow-personalisation-container {
    display: flex;
    height: 3rem;
    align-items: center;
    justify-content: center;
  }

  .ph__card-container {
    @media (max-width: $screen-lg) {
      margin-bottom: 20px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .ph__card {
    box-shadow: $simplified-box-shadow-config;
    border-radius: $default-border-radius;
    position: relative;
    margin-bottom: 20px;

    &.ph__action-card {
      margin-bottom: 0;
      padding: 10px;
      display: flex;
      flex-direction: column;

      &:last-child {
        margin-right: 0;
      }

      .ph__action-card-image {
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center center;
        width: 100%;
      }
    }

    &.with-tabs {
      background-color: var(--primary-extra-lightest);
    }
  }

  .ph__welcome-text {
    padding: 20px 70px;
  }

  h4.h4 {
    margin-bottom: 10px;
    text-align: center;
  }

  p {
    margin-bottom: 20px;
    line-height: 15px;
    letter-spacing: 0.5px;
    text-align: center;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ph__tabs-content {
    padding: 20px;
  }

  :deep(.button__label) {
    overflow-x: hidden;
    display: block;
    text-overflow: ellipsis;
    text-align: center;
  }

  .ph__card-title {
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    height: 16px;
    font-size: 14px;
    text-overflow: ellipsis;
    margin: 10px 0;
  }

  .ph__card-description {
    $lineHeight: 15px;
    $nrOfLines: 3;
    display: -webkit-box;
    width: 100%;
    height: $nrOfLines*$lineHeight;
    -webkit-line-clamp: $nrOfLines;
    line-height: $lineHeight;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    margin-bottom: 15px;

    &.announcement-description {
      $lineHeight: 15px;
      $nrOfLines: 6;
      height: $nrOfLines*$lineHeight;
      -webkit-line-clamp: $nrOfLines;
    }

    &.resource-description {
      $lineHeight: 15px;
      $nrOfLines: 6;
      height: $nrOfLines*$lineHeight;
      -webkit-line-clamp: $nrOfLines;
    }

    p {
      text-align: left;
    }
  }

  hr {
    flex-grow: 1;
    border: 1px solid var(--primary-extra-lightest);
    height: 0;
  }

  .ph__relevant-yes-no {
    margin: 10px 0;
    font-size: 12px;

    span {
      line-height: 10px;
      border: 1px solid var(--primary);
      border-radius: $default-border-radius-narrow;
      margin: 5px 5px 10px 0;
      cursor: pointer;
      padding: 5px 10px;
      display: inline-block;

      &.is-active {
        background: var(--primary-extra-lightest);
      }
    }
  }

  .ph__announcement-creator {
    display: flex;

    .ph__announcement-creator-name {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow-x: hidden;
      line-height: 22px;
    }
  }

  .ph__resource-title {
    font-size: 14px;
    font-weight: 500;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  :deep(.multiselect.multiselect--datascouts) {
    .multiselect__tags {
      padding: 0;
      border: 0
    }

    &.multiselect--active {
      .multiselect__tags {
        box-shadow: none !important; // important to override important in _focus-state.scss
      }
    }

    .multiselect__content-wrapper {
      display: none;
    }

    .multiselect__placeholder {
      display: none;
    }
  }

  .ph__resource-description {
    $lineHeight: 16px;
    $nrOfLines: 2;
    margin-bottom: 10px;
    margin-top: 5px;
    $lineHeight: 16px;
    $nrOfLines: 6;
    display: -webkit-box;
    width: 100%;
    height: $nrOfLines*$lineHeight;
    -webkit-line-clamp: $nrOfLines;
    line-height: $lineHeight;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
  }
</style>
